import { faFloppyDisk, faPen, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';

import CheckPermissions from '@/components/CheckPermissions';
import LargeCard from '@/components/LargeCard';
import { PERMISSIONS } from '@/constants/auth.constants';

import type {
  EmailConfigurationFormValues,
  EmailConfigurationProps
} from './EmailConfiguration.types';

const EmailConfiguration = (props: EmailConfigurationProps) => {
  const { orgInfo, handleSave } = props;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const timezones: string[] = Intl.supportedValuesOf('timeZone');
  const timezoneRegions: string[] = [
    ...new Set(
      timezones.map((tz: string) => {
        const [region] = tz.split('/');
        return region;
      })
    )
  ];
  const defaultFormValues = {
    use_plaintext: orgInfo.use_plaintext === true,
    use_modern_email_template: orgInfo.use_modern_email_template === true,
    region: orgInfo.timezone ? orgInfo.timezone.split('/')[0] : '',
    timezone: orgInfo.timezone || ''
  };
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty }
  } = useForm<EmailConfigurationFormValues>({
    defaultValues: defaultFormValues,
    values: defaultFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const selectedRegion = useWatch({ control, name: 'region' });
  const usePlaintext = useWatch({ control, name: 'use_plaintext' });

  useEffect(() => {
    if (defaultFormValues.timezone.split('/')[0] !== selectedRegion) {
      setValue('timezone', '');
    }
  }, [selectedRegion]);

  const availableTimezones = useMemo(
    () => timezones.filter((tz: string) => tz.startsWith(selectedRegion ?? '')),
    [selectedRegion]
  );

  const onSubmit = async (data: EmailConfigurationFormValues) => {
    if (isSaving) return;

    setIsSaving(true);
    if (!isDirty) {
      setIsEditing(false);
      setIsSaving(false);
      return;
    }

    try {
      await handleSave({
        ...orgInfo,
        use_plaintext: data.use_plaintext,
        use_modern_email_template: data.use_modern_email_template,
        timezone: data.timezone
      });
      setIsEditing(false);
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <LargeCard
      title="Email Configuration"
      rightContent={
        <CheckPermissions permissions={PERMISSIONS.ADMIN_UPDATE}>
          {isEditing ? (
            <Stack direction="row">
              <IconButton onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                <FontAwesomeIcon size="xs" icon={faFloppyDisk} />
              </IconButton>
              <IconButton
                onClick={() => {
                  setIsEditing(false);
                  reset(defaultFormValues);
                }}
              >
                <FontAwesomeIcon size="xs" icon={faXmark} />
              </IconButton>
            </Stack>
          ) : (
            <IconButton onClick={() => setIsEditing(true)}>
              <FontAwesomeIcon size="xs" icon={faPen} />
            </IconButton>
          )}
        </CheckPermissions>
      }
    >
      <Grid container spacing={3} sx={{ paddingBottom: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InputLabel htmlFor="use_plaintext" sx={{ marginBottom: 1 }}>
            Email Notification Format
          </InputLabel>
          <Controller
            name="use_plaintext"
            control={control}
            render={({ field }) => (
              <ToggleButtonGroup
                id="use_plaintext"
                size="small"
                color="primary"
                value={field.value}
                exclusive
                disabled={!isEditing}
                onChange={(_event, newValue) => {
                  if (newValue !== null) field.onChange(newValue);
                }}
                sx={{ width: '100%' }}
              >
                <ToggleButton sx={{ width: '50%' }} value={false}>
                  HTML
                </ToggleButton>
                <ToggleButton sx={{ width: '50%' }} value>
                  Plain Text
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          />
        </Grid>
        {!usePlaintext && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <InputLabel
              htmlFor="use_modern_email_template"
              sx={{ marginBottom: 1 }}
            >
              Email Template Design
            </InputLabel>
            <Controller
              name="use_modern_email_template"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  id="use_modern_email_template"
                  size="small"
                  color="primary"
                  value={field.value}
                  exclusive
                  disabled={!isEditing}
                  onChange={(_event, newValue) => {
                    if (newValue !== null) field.onChange(newValue);
                  }}
                  sx={{ width: '100%' }}
                >
                  <ToggleButton sx={{ width: '50%' }} value={false}>
                    Legacy
                  </ToggleButton>
                  <ToggleButton sx={{ width: '50%' }} value>
                    Modern
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InputLabel htmlFor="region" sx={{ marginBottom: 1 }}>
            Timezone Region
          </InputLabel>
          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <Select
                id="region"
                disabled={!isEditing}
                sx={{ width: '100%' }}
                error={!!errors.region}
                {...field}
              >
                <MenuItem value="">None</MenuItem>
                {timezoneRegions.map((region: string) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <InputLabel htmlFor="timezone" sx={{ marginBottom: 1 }}>
            Timezone
          </InputLabel>
          <Controller
            name="timezone"
            control={control}
            render={({ field }) => (
              <Select
                id="timezone"
                disabled={!isEditing}
                sx={{ width: '100%' }}
                error={!!errors.timezone}
                {...field}
              >
                <MenuItem value="">None</MenuItem>
                {availableTimezones.map((tz: string) => (
                  <MenuItem key={tz} value={tz}>
                    {tz}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Grid>
      </Grid>
    </LargeCard>
  );
};

export default EmailConfiguration;
